import {
  COUNT_BIRD,
  COUNT_BIRD_MEDIUM,
  COUNT_BIRD_MIN_FULL_PX,
  COUNT_BIRD_MIN_MEDIUM_PX,
  COUNT_BIRD_SMALL,
  MARK_ROLE_TOKENS,
  type MarkRole,
  type MarkShape,
} from '@league/shared/marks';

/**
 * The Count — a One Bam bird whose tail feathers are grouped one, five, two.
 *
 * THE MARK DOES NOT VARY BY SCREEN. IT VARIES BY GROUND. Which rendition you get is decided by
 * what is behind it, never by which page you are on. A logo that changes with the screen is not a
 * logo; it is decoration, and it stops being recognisable as an identity.
 *
 * THE FEATHER RULE IS NOT OPTIONAL. One feather signal, then a group in the ground's label grey,
 * then two in the body colour.
 *
 * GROUPING carries the count and colour reinforces it — not the other way round, which is how it
 * was built and why it read 1–5. The two body-coloured feathers sit at the same value as the bird
 * beside them, so on colour alone the third group was invisible; the spacing now does the work.
 * It lives in the geometry (see `marks.ts`) so a caller cannot recolour or regroup by accident —
 * there is no prop for either.
 *
 * Inlined rather than an `<img>`, so `mono` can inherit `currentColor` and so the whole mark can be
 * hidden from a screen reader where a wordmark beside it already says the name.
 */
export type CountBirdGround = 'navy' | 'light' | 'mono';

export interface CountBirdProps {
  /**
   * Rendered WIDTH in px; the height follows the drawing's own proportions. All three renditions are
   * wider than they are tall, so forcing a square box would letterbox the bird and leave a band of
   * empty space that reads as bad alignment in a nav lockup.
   *
   * The size also picks the drawing — 72+ full, 40–71 medium, under 40 small. See below.
   *
   * TO SIZE FLUIDLY, pass the width the mark reaches at its LARGEST and override with CSS —
   * `className="h-[clamp(2.4rem,5vw,3.1rem)] w-auto"`. A class beats the width/height attributes,
   * and `size` still picks the right drawing for the biggest it will get.
   */
  size: number;
  /** What is BEHIND the mark, not which page it is on. */
  ground: CountBirdGround;
  /**
   * The product name, for screen readers. Passed in rather than written here: the name is
   * provisional and lives in one config value, and a mark with the name baked into it is exactly
   * the thing that survives a rename. `brand-literal.test.ts` fails the build if it appears.
   *
   * OMIT IT wherever a wordmark beside the bird already carries the name — the mark then goes
   * `aria-hidden` and the text does the work, instead of the name being announced twice.
   */
  label?: string;
  className?: string;
}

/**
 * ⚠ THE ROLE-TO-TOKEN TABLE MOVED TO `marks.ts` ON 2026-09-12, and the move is the point.
 *
 * This component held it as `var(--brand-*)` strings; the standalone SVG emitter needed the same
 * answers as literal hex, because a `.svg` a designer opens has no stylesheet behind it. So the
 * mapping existed twice in two forms — the arrangement that let the exported logos drift from the
 * on-screen mark and ship a bird counting to six. `MARK_ROLE_TOKENS` is now the one table, and this
 * only does the CSS half of the resolution.
 *
 * `mono` resolves nothing: one flat colour inherits `currentColor` throughout and knocks the eye
 * out, which is what keeps the head reading as a head. See the note on `MARK_ROLE_TOKENS`.
 */
const kebab = (s: string) =>
  s.replace(/([A-Z])|(\d+)/g, (_, c: string, d: string) => `-${c ? c.toLowerCase() : d}`);

const MONO: Record<MarkRole, string> = {
  signal: 'currentColor',
  count: 'currentColor',
  body: 'currentColor',
  eye: 'none',
};

function coloursFor(ground: CountBirdGround): Record<MarkRole, string> {
  if (ground === 'mono') return MONO;

  const tokens = MARK_ROLE_TOKENS[ground];
  return {
    signal: `var(--brand-${kebab(tokens.signal)})`,
    count: `var(--brand-${kebab(tokens.count)})`,
    body: `var(--brand-${kebab(tokens.body)})`,
    eye: `var(--brand-${kebab(tokens.eye)})`,
  };
}

function Shape({ shape, colours }: { shape: MarkShape; colours: Record<MarkRole, string> }) {
  if (shape.kind === 'line') {
    return (
      <line
        x1={shape.x1}
        y1={shape.y1}
        x2={shape.x2}
        y2={shape.y2}
        stroke={colours[shape.role]}
        strokeWidth={shape.width}
        strokeLinecap="round"
      />
    );
  }

  if (shape.kind === 'circle') {
    return <circle cx={shape.cx} cy={shape.cy} r={shape.r} fill={colours[shape.role]} />;
  }

  return (
    <path
      d={shape.d}
      fill={shape.fill ? colours[shape.fill] : 'none'}
      stroke={shape.stroke ? colours[shape.stroke] : undefined}
      strokeWidth={shape.width}
      strokeLinecap="round"
    />
  );
}

export function CountBird({ size, ground, label, className }: CountBirdProps) {
  /**
   * THREE DRAWINGS, NOT ONE SCALED — and the switch is automatic, because leaving it to the caller
   * means somebody eventually ships the eight-feather mark at 24px where the tail is a smudge.
   *
   *   72px and up   the full count, all eight feathers
   *   40–71px       1 / 3 / 2 in heavier strokes — grouping kept, exact count conceded
   *   under 40px    three feathers, no crest, no legs; the count abandoned on purpose
   *
   * The middle band exists because the mark ships at 54px and 48px — the public header and the app
   * rail — and both used to get the eight-feather drawing, where the count feathers merge. The two
   * sizes most people ever see were the two the count did not survive.
   */
  const mark =
    /*
     * MONO SKIPS THE MIDDLE RENDITION, and the reason is that the middle rendition only exists to
     * protect the count.
     *
     * One colour has no 1/5/2 to lose — the Asset Kit says the count is "deliberately abandoned"
     * there — so the eight-feather silhouette is simply the better drawing at any size it is still
     * legible at. This is what the approved public design does in its masthead, and it was right:
     * it sidesteps the whole illegible-at-54px problem rather than working around it.
     *
     * Below 40px it still drops to the small drawing. That threshold is about strokes merging into
     * a smudge, which happens whatever colour they are.
     */
    ground === 'mono'
      ? size >= COUNT_BIRD_MIN_MEDIUM_PX
        ? COUNT_BIRD
        : COUNT_BIRD_SMALL
      : size >= COUNT_BIRD_MIN_FULL_PX
        ? COUNT_BIRD
        : size >= COUNT_BIRD_MIN_MEDIUM_PX
          ? COUNT_BIRD_MEDIUM
          : COUNT_BIRD_SMALL;
  const colours = coloursFor(ground);

  return (
    <svg
      viewBox={mark.viewBox}
      width={size}
      /*
       * NOT ROUNDED. The two attributes together are what a browser uses as the intrinsic aspect
       * ratio when CSS sets one dimension and leaves the other `auto` — so rounding here quietly
       * distorts a fluidly-sized mark. It rendered 0.6px narrower than the approved design at the
       * same height, which is exactly the kind of drift nobody would ever find by looking.
       */
      height={size / mark.aspect}
      className={className}
      role={label ? 'img' : undefined}
      aria-hidden={label ? undefined : true}
      focusable="false"
    >
      {label ? <title>{label}</title> : null}
      {mark.shapes.map((shape, i) => (
        <Shape key={i} shape={shape} colours={colours} />
      ))}
    </svg>
  );
}
